import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs } from 'firebase/firestore';
import './ClientId.css';
import { 
  FaUsers, 
  FaSearch,
  FaSort, 
  FaSortUp, 
  FaSortDown, 
  FaEye, 
  FaTimes, 
  FaUser, 
  FaCalendarAlt, 
  FaEuroSign, 
  FaShoppingCart, 
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaArrowUp,
  FaArrowDown,
  FaList,
  FaChartBar,
  FaFileExcel
} from 'react-icons/fa';

const ClientId = () => {
  const [searchId, setSearchId] = useState('');
  const [allClients, setAllClients] = useState([]);
  const [filteredClients, setFilteredClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [popupVisible, setPopupVisible] = useState(false);
  const [sortCriteria, setSortCriteria] = useState('date');
  const [sortDirection, setSortDirection] = useState('desc');
  const [showStats, setShowStats] = useState(false);
  const [viewMode, setViewMode] = useState('table'); // 'table' ou 'cards'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchAllClients = async () => {
      setLoading(true);
      try {
        const totalCommandeRef = collection(db, 'total_commande');
        const querySnapshot = await getDocs(totalCommandeRef);
        const clients = [];
        
        querySnapshot.forEach((doc) => {
          clients.push({ id: doc.id, ...doc.data() });
        });
        
        setAllClients(clients);
        setFilteredClients(clients.sort((a, b) => b.date.toDate() - a.date.toDate()));
      } catch (error) {
        console.error('Erreur lors du chargement des clients :', error);
      }
      setLoading(false);
    };

    fetchAllClients();
  }, []);

  // Fonction pour rechercher les commandes avec l'ID client
  const handleSearch = (searchValue) => {
    if (!searchValue.trim()) {
      setFilteredClients(allClients);
      setCurrentPage(1);
      return;
    }

    const searchLower = searchValue.trim().toLowerCase();
    const filtered = allClients.filter(client => 
      client.idclient.toString().toLowerCase().includes(searchLower) ||
      client.prenom.toLowerCase().includes(searchLower) ||
      client.date.toDate().toLocaleString().toLowerCase().includes(searchLower) ||
      client.total.toString().includes(searchLower)
    );
    setFilteredClients(filtered);
    setCurrentPage(1);
  };

  // Calculer les statistiques
  const calculateStats = () => {
    const totalClients = filteredClients.length;
    const totalRevenue = filteredClients.reduce((sum, client) => sum + client.total, 0);
    const averageOrderValue = totalClients > 0 ? totalRevenue / totalClients : 0;
    const uniqueClients = new Set(filteredClients.map(client => client.idclient)).size;
    
    return {
      totalClients,
      totalRevenue,
      averageOrderValue,
      uniqueClients
    };
  };

  // Pagination
  const getPaginatedClients = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredClients.slice(startIndex, endIndex);
  };

  const totalPages = Math.ceil(filteredClients.length / itemsPerPage);

  // Export Excel
  const exportToExcel = async () => {
    setExporting(true);
    try {
      const XLSX = await import('xlsx');
      
      const excelData = filteredClients.map(client => ({
        'ID Client': client.idclient,
        'Prénom': client.prenom,
        'Date': client.date.toDate().toLocaleDateString('fr-FR'),
        'Heure': client.date.toDate().toLocaleTimeString('fr-FR'),
        'Total': client.total,
        'Nombre d\'articles': client.items ? client.items.length : 0
      }));

      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Clients");
      
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `clients-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
    } finally {
      setExporting(false);
    }
  };

  // Fonction pour afficher les commandes du client
  const handleClientClick = (clientId) => {
    const clientOrders = allClients.filter(client => client.idclient === clientId);
    setSelectedClient(clientOrders);
    setPopupVisible(true);
  };

  // Calculer le total global
  const calculateTotalGlobal = (orders) => {
    return orders.reduce((total, order) => total + order.total, 0);
  };

  // Fermer la popup
  const closePopup = () => {
    setPopupVisible(false);
    setSelectedClient(null);
  };

  const sortClients = (criteria) => {
    const newDirection = criteria === sortCriteria 
      ? sortDirection === 'asc' ? 'desc' : 'asc'
      : 'desc';
    
    setSortDirection(newDirection);
    setSortCriteria(criteria);
    
    const sortedClients = [...filteredClients].sort((a, b) => {
      let comparison = 0;
      if (criteria === 'date') {
        comparison = b.date.toDate() - a.date.toDate();
      } else if (criteria === 'idclient') {
        comparison = a.idclient - b.idclient;
      } else if (criteria === 'prenom') {
        comparison = a.prenom.localeCompare(b.prenom);
      } else if (criteria === 'total') {
        comparison = a.total - b.total;
      }
      return newDirection === 'asc' ? comparison : -comparison;
    });
    
    setFilteredClients(sortedClients);
    setCurrentPage(1);
  };

  const getSortIcon = (criteria) => {
    if (sortCriteria !== criteria) {
      return <FaSort />;
    }
    return sortDirection === 'asc' ? <FaSortUp /> : <FaSortDown />;
  };

  const stats = calculateStats();
  const paginatedClients = getPaginatedClients();

  return (
    <div className="client-id-container">
      {/* Header */}
      <div className="client-id-header">
        <div className="client-id-title-section">
          <FaUsers className="client-id-title-icon" />
          <h1 className="client-id-title">Gestion des Clients</h1>
        </div>
        <p className="client-id-subtitle">
          Suivi et analyse des commandes clients
        </p>
      </div>

      {/* Statistiques */}
      {showStats && (
        <div className="client-id-stats-section">
          <div className="client-id-stats-grid">
            <div className="client-id-stat-card">
              <div className="client-id-stat-icon">
                <FaUsers />
              </div>
              <div className="client-id-stat-content">
                <div className="client-id-stat-value">{stats.totalClients}</div>
                <div className="client-id-stat-label">Commandes</div>
              </div>
            </div>
            <div className="client-id-stat-card">
              <div className="client-id-stat-icon">
                <FaUser />
              </div>
              <div className="client-id-stat-content">
                <div className="client-id-stat-value">{stats.uniqueClients}</div>
                <div className="client-id-stat-label">Clients Uniques</div>
              </div>
            </div>
            <div className="client-id-stat-card">
              <div className="client-id-stat-icon">
                <FaEuroSign />
              </div>
              <div className="client-id-stat-content">
                <div className="client-id-stat-value">{stats.totalRevenue.toFixed(2)}€</div>
                <div className="client-id-stat-label">Chiffre d'Affaires</div>
              </div>
            </div>
            <div className="client-id-stat-card">
              <div className="client-id-stat-icon">
                <FaChartBar />
              </div>
              <div className="client-id-stat-content">
                <div className="client-id-stat-value">{stats.averageOrderValue.toFixed(2)}€</div>
                <div className="client-id-stat-label">Panier Moyen</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Contrôles */}
      <div className="client-id-controls-section">
        <div className="client-id-filters">
          <div className="client-id-search-container">
            <FaSearch className="client-id-search-icon" />
            <input
              type="text"
              placeholder="Rechercher un client..."
              value={searchId}
              onChange={(e) => {
                setSearchId(e.target.value);
                handleSearch(e.target.value);
              }}
              className="client-id-search-input"
            />
          </div>
        </div>

        <div className="client-id-actions">
          <button
            onClick={() => setShowStats(!showStats)}
            className="client-id-toggle-stats-btn"
          >
            {showStats ? <FaArrowUp /> : <FaArrowDown />}
            {showStats ? 'Masquer' : 'Afficher'} Stats
          </button>
          
          <button
            onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
            className="client-id-view-mode-btn"
          >
            {viewMode === 'table' ? <FaList /> : <FaChartBar />}
            {viewMode === 'table' ? 'Vue Cartes' : 'Vue Tableau'}
          </button>
          
          <button
            onClick={exportToExcel}
            disabled={exporting}
            className="client-id-export-btn"
          >
            {exporting ? <FaSpinner className="spinning" /> : <FaFileExcel />}
            {exporting ? 'Export...' : 'Exporter'}
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="client-id-loading-container">
          <div className="client-id-loading-spinner">
            <FaSpinner className="spinning" />
          </div>
          <p className="client-id-loading-text">Chargement des clients...</p>
        </div>
      )}

      {/* Contenu principal */}
      {!loading && (
        <>
          {viewMode === 'table' ? (
            <div className="client-id-table-section">
              <div className="client-id-table-container">
                <table className="client-id-table">
                  <thead>
                    <tr>
                      <th onClick={() => sortClients('idclient')} className="client-id-sortable-header">
                        <div className="client-id-header-content">
                          <span>ID Client</span>
                          {getSortIcon('idclient')}
                        </div>
                      </th>
                      <th onClick={() => sortClients('prenom')} className="client-id-sortable-header">
                        <div className="client-id-header-content">
                          <span>Prénom</span>
                          {getSortIcon('prenom')}
                        </div>
                      </th>
                      <th onClick={() => sortClients('date')} className="client-id-sortable-header">
                        <div className="client-id-header-content">
                          <span>Date</span>
                          {getSortIcon('date')}
                        </div>
                      </th>
                      <th onClick={() => sortClients('total')} className="client-id-sortable-header">
                        <div className="client-id-header-content">
                          <span>Total</span>
                          {getSortIcon('total')}
                        </div>
                      </th>
                      <th className="client-id-sortable-header">
                        <div className="client-id-header-content">
                          <span>Actions</span>
                          <FaSort className="client-id-sort-icon-disabled" />
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedClients.map((client) => (
                      <tr key={client.id} className="client-id-table-row">
                        <td className="client-id-id-cell">{client.idclient}</td>
                        <td className="client-id-name-cell">
                          <div className="client-id-client-info">
                            <div className="client-id-client-avatar">
                              <FaUser />
                            </div>
                            <span>{client.prenom}</span>
                          </div>
                        </td>
                        <td className="client-id-date-cell">
                          <div className="client-id-date-info">
                            <FaCalendarAlt className="client-id-date-icon" />
                            <span>{client.date.toDate().toLocaleDateString('fr-FR')}</span>
                          </div>
                        </td>
                        <td className="client-id-total-cell">
                          <div className="client-id-total-info">
                            <FaEuroSign className="client-id-total-icon" />
                            <span>{client.total}€</span>
                          </div>
                        </td>
                        <td className="client-id-actions-cell">
                          <button
                            onClick={() => handleClientClick(client.idclient)}
                            className="client-id-view-btn"
                            title="Voir les détails"
                          >
                            <FaEye />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="client-id-cards-section">
              <div className="client-id-cards-grid">
                {paginatedClients.map((client) => (
                  <div key={client.id} className="client-id-card">
                    <div className="client-id-card-header">
                      <div className="client-id-card-avatar">
                        <FaUser />
                      </div>
                      <div className="client-id-card-info">
                        <h3 className="client-id-card-name">{client.prenom}</h3>
                        <p className="client-id-card-id">ID: {client.idclient}</p>
                      </div>
                    </div>
                    <div className="client-id-card-body">
                      <div className="client-id-card-detail">
                        <FaCalendarAlt className="client-id-card-icon" />
                        <span>{client.date.toDate().toLocaleDateString('fr-FR')}</span>
                      </div>
                      <div className="client-id-card-detail">
                        <FaEuroSign className="client-id-card-icon" />
                        <span>{client.total}€</span>
                      </div>
                      <div className="client-id-card-detail">
                        <FaShoppingCart className="client-id-card-icon" />
                        <span>{client.items ? client.items.length : 0} articles</span>
                      </div>
                    </div>
                    <div className="client-id-card-actions">
                      <button
                        onClick={() => handleClientClick(client.idclient)}
                        className="client-id-card-view-btn"
                      >
                        <FaEye />
                        Voir détails
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="client-id-pagination">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="client-id-pagination-btn"
              >
                Précédent
              </button>
              <span className="client-id-pagination-info">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="client-id-pagination-btn"
              >
                Suivant
              </button>
            </div>
          )}

          {/* Message si aucun résultat */}
          {filteredClients.length === 0 && (
            <div className="client-id-no-results">
              <FaExclamationTriangle className="client-id-no-results-icon" />
              <p>Aucun client trouvé pour cette recherche.</p>
            </div>
          )}
        </>
      )}

      {/* Popup de détails */}
      {popupVisible && (
        <div className="client-id-popup-overlay">
          <div className="client-id-popup">
            <div className="client-id-popup-header">
              <h2 className="client-id-popup-title">
                <FaShoppingCart className="client-id-popup-icon" />
                Détails des commandes
              </h2>
              <button className="client-id-popup-close-btn" onClick={closePopup}>
                <FaTimes />
              </button>
            </div>
            <div className="client-id-popup-body">
              {selectedClient && selectedClient.length > 0 ? (
                <>
                  <div className="client-id-orders-list">
                    {selectedClient.map((order, index) => (
                      <div key={index} className="client-id-order-card">
                        <div className="client-id-order-header">
                          <h3>Commande #{order.idclient}</h3>
                          <span className="client-id-order-total">{order.total}€</span>
                        </div>
                        <div className="client-id-order-details">
                          <p><strong>Client :</strong> {order.prenom}</p>
                          <p><strong>Date :</strong> {order.date.toDate().toLocaleString('fr-FR')}</p>
                        </div>
                        <div className="client-id-order-items">
                          <h4>Articles commandés :</h4>
                          <ul>
                            {order.items.map((item, idx) => (
                              <li key={idx} className="client-id-order-item">
                                <span className="client-id-item-name">{item.nom}</span>
                                <span className="client-id-item-quantity">x{item.quantity}</span>
                                <span className="client-id-item-price">{item.prix.toFixed(2)}€</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="client-id-popup-total">
                    <FaCheckCircle className="client-id-total-icon" />
                    <span>Total global : {calculateTotalGlobal(selectedClient)}€</span>
                  </div>
                </>
              ) : (
                <div className="client-id-no-orders">
                  <FaExclamationTriangle />
                  <p>Aucune commande trouvée.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientId;
